import { useEffect, useRef, useState } from 'react';
import { waitForLayout, getEmbeddedFontCss, renderElementToBlob, yieldToBrowser } from '../utils/imageExportUtil';

/**
 * Turns a set of mounted, off-screen templates into PNGs in the background,
 * one at a time, handing the browser back control between images so the rest
 * of the screen stays clickable while they render.
 *
 * Any change to `runKey` cancels the run in progress — its pending results
 * are thrown away, not shown — clears the images, and starts again. That is
 * how a new date range, new image settings or a Regenerate press take effect
 * immediately instead of queueing behind the old run. An image already
 * inside the browser's renderer finishes in the background and is discarded.
 *
 * Each image is held as an object URL (see renderElementToBlob) and revoked
 * once it is replaced or the hook unmounts.
 *
 * @param {object} params
 * @param {boolean} params.enabled - false clears everything and renders nothing
 * @param {Array<{id: string, width: number, height: number}>} params.templates - in render order
 * @param {{current: Object<string, HTMLElement>}} params.nodesRef - template id -> mounted template root
 * @param {*} params.runKey - a new value (by identity) restarts the run
 * @returns {{images: Object<string, string>, errors: Object<string, string>, generating: boolean}}
 *   images: template id -> object URL; errors: template id -> message
 */
export function useImageBatchRenderer({ enabled, templates, nodesRef, runKey }) {
  const [images, setImages] = useState({});
  const [errors, setErrors] = useState({});
  const [generating, setGenerating] = useState(false);
  const urlsRef = useRef([]);
  // Read inside the run rather than listed as a dependency: the template list
  // is derived from the same inputs as runKey, and a fresh array each render
  // must not restart the run.
  const templatesRef = useRef(templates);
  templatesRef.current = templates;

  useEffect(() => {
    const revokeAll = () => {
      urlsRef.current.forEach(url => URL.revokeObjectURL(url));
      urlsRef.current = [];
    };
    revokeAll();
    setImages({});
    setErrors({});
    const list = templatesRef.current || [];
    if (!enabled || list.length === 0) {
      setGenerating(false);
      return undefined;
    }

    let cancelled = false;
    setGenerating(true);

    const run = async () => {
      await waitForLayout();
      if (cancelled) return;
      const fontEmbedCSS = await getEmbeddedFontCss(nodesRef.current[list[0].id]);
      // One at a time, not Promise.all: each render holds a full-size
      // canvas, and several at once is enough to stall a low-end phone.
      for (const template of list) {
        await yieldToBrowser();
        if (cancelled) return;
        try {
          const blob = await renderElementToBlob(nodesRef.current[template.id], {
            width: template.width,
            height: template.height,
            fontEmbedCSS,
          });
          if (cancelled) return;
          const url = URL.createObjectURL(blob);
          urlsRef.current.push(url);
          setImages(prev => ({ ...prev, [template.id]: url }));
        } catch (error) {
          if (cancelled) return;
          console.error(`Availability image "${template.id}" failed:`, error);
          setErrors(prev => ({ ...prev, [template.id]: error.message }));
        }
      }
      if (!cancelled) setGenerating(false);
    };

    run();
    return () => { cancelled = true; };
    // nodesRef is a ref; templates are read through templatesRef (see above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, runKey]);

  // Free the last run's images when the dialog goes away.
  useEffect(() => () => {
    urlsRef.current.forEach(url => URL.revokeObjectURL(url));
    urlsRef.current = [];
  }, []);

  return { images, errors, generating };
}

export default useImageBatchRenderer;
